<script lang="ts">
  import WishlistButton from '$lib/components/product/WishlistButton.svelte'
  import PromoBadge from '$lib/components/common/PromoBadge.svelte'
  import { PUBLIC_BIXOTO_MEDIA } from '$env/static/public';
	import {
		is_set_array,
		is_set_object,
	} from '$lib/js/helpers'
  import { page } from '$app/state';
  import type { FullProduct, SizedImage } from "$lib/js/elasticsearch/types";

  
  interface Props {
    /* Image modal */
    product: FullProduct;
    showImg?: boolean;
    imgIndex?: number;
  }

  let { product, showImg = $bindable(false), imgIndex = $bindable(0) }: Props = $props();

  const parent = page.data.parent

  let productGallery: {
    resized_image: SizedImage,
    resized_small_image: SizedImage,
  }[] = $state([])

  if( is_set_array(product.media_gallery) ) {
    // Exclude videos
    productGallery = product.media_gallery.filter(entry => !entry.vid)
  } else if (is_set_object(parent)) {
    productGallery = parent.media_gallery.filter(entry => !entry.vid)
  } else {
    productGallery.push(product)
  }

  function zoomImage(i: number, e: MouseEvent) {
    const { target } = e;
    if(target instanceof HTMLElement) {
      let tar = target.className

      showImg = (!tar.includes('slide') && !tar.includes('slide-icon'))
      imgIndex = i;
    }
  }

  function goToSlide(i: number) {
    imgIndex = i
  }

  function goToPrev() {
    imgIndex = imgIndex !== 0 ? (imgIndex - 1) : (productGallery.length - 1)
  }

  function goToNext() {
    imgIndex = imgIndex === (productGallery.length - 1) ? 0 : (imgIndex + 1)
  }

  // Fix CLS by setting imgs width & height
  let img_size = $derived(page.data.mobile === true ? 80 : 140)
  let img_main_size = $derived(page.data.mobile === true ? 340 : 700)
</script>


<svelte:head>
  {#if is_set_object(productGallery) && productGallery[0]?.resized_image?.path}
    <link rel="preload" as="image" href="{PUBLIC_BIXOTO_MEDIA}{productGallery[0].resized_image?.path}">
  {/if}
</svelte:head>


<div class="product-img">
  {#if productGallery.length > 1}
    <div class="product-img__gallery">
      <div class="product-img__gallery__inner">
        {#each productGallery as entry, i}
          {#if entry.resized_small_image}
            <button class:selected={imgIndex === i} onclick={() => goToSlide(i)}>
              <img
                src="{PUBLIC_BIXOTO_MEDIA}{entry.resized_small_image.path}"
                alt="{product.name}"
                title="{product.name}"
                width="{img_size}"
                height="{img_size}"
              >
            </button>
          {/if}
        {/each}
      </div>
    </div>
  {/if}

  {#if is_set_array(productGallery)}
    <button class="product-img__main" onclick={(e) => zoomImage(imgIndex, e)}>
      {#if productGallery.length > 1}
        <button onclick={() => goToPrev()} class="slide prev">
          <span class="slide-icon icon icon-left"></span>
        </button>
      {/if}

      <img
        src="{PUBLIC_BIXOTO_MEDIA}{productGallery[imgIndex].resized_image?.path}"
        alt="{product.name}"
        title="{product.name}"
        width="{img_main_size}"
        height="{img_main_size}"
      >

      {#if productGallery.length > 1}
        <button onclick={() => goToNext()} class="slide next">
          <span class="slide-icon icon icon-right"></span>
        </button>
      {/if}
    </button>
  {/if}
  <WishlistButton {product} type="product" />
</div>

<style lang="scss">
  .product-img {
    position: relative;
    display: flex;
    justify-content: space-between;
    flex-direction: column-reverse;
    margin-bottom: 30px;

    @media (min-width: 1025px) {
      flex-direction: row;
    }

    &__gallery {
      @media (max-width: 1024px) {
        margin-top: 30px;
        height: auto !important;
      }

      @media (min-width: 1025px) {
        position: relative;
        display: table;
        overflow: hidden;
        width: 140px;
        min-width: 140px;
        min-height: 480px;

        &::after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 0;
          height: 50px;
          width: 142px;
          background: linear-gradient(rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
          pointer-events: none;
        }

        &__inner {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow-y: scroll;
          -ms-overflow-style: none; /* IE and Edge */
          scrollbar-width: none; /* Firefox */

          &::-webkit-scrollbar {
            display: none;
          }
        }
      }

      @media (min-width: 1420px) {
        min-height: 700px;
      }

      button {
        margin-bottom: 6px;
        border: 2px solid white;

        @media (max-width: 1024px) {
          width: 20%;
          max-width: 140px;
        }

        &.selected {
          border: 2px solid var(--main);
        }

        img {
          display: flex;
          width: 100%;
          height: auto;
          transition: all linear 0.125s;
        }
      }
    }

    &__main {
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;

      @media (min-width: 1025px) {
        display: flex;
        justify-content: center;
        width: calc(100% - 190px);
      }

      img {
        padding: 0 30px;
        width: calc(100% - 60px);
        max-width: 640px;
        max-height: 640px;
        object-fit: contain;
        height: auto;
        transition: all linear 0.125s;

        @media (min-width: 769px) {
          padding: 30px;
        }

        &:hover {
          @media (min-width: 769px) {
            transform: scale(1.05);
          }
        }
      }

      button.slide {
        position: absolute;
        width: 30px;
        height: 30px;
        border-radius: 30px;
        top: calc(50% - 15px);
        font-size: 10px;
        color: #ff4200;
        font-weight: 700;
        background: rgba(255, 66, 0, .2);
        z-index: 1;

        @media (min-width: 769px) {
          width: 45px;
          height: 45px;
          border-radius: 45px;
          top: calc(50% - 22px);
          font-size: 14px;
        }

        &.prev {
          left: 0;

          .icon {
            margin-right: 1px;

            @media (min-width: 769px) {
              margin-right: 3px;
            }
          }
        }

        &.next {
          right: 0;

          .icon {
            margin-left: 1px;

            @media (min-width: 769px) {
              margin-left: 3px;
            }
          }
        }
      }
    }
  }
</style>
